import { useEffect, useRef, useState } from 'react';

import { useAvatarManifest } from '../../assets/data/avatarManifest';
import { IAsset } from '../../utils/interfaces/DbInterfaces';
import SectionWrapper from '../SectionWrapper';

import styles from './Achievements.module.scss';

interface IAchievementsProps {
  assets: IAsset[];
  className?: string;
}

// Helper function to get asset name from manifest
const getAssetName = (assetId: string, avatarManifest: Record<string, any>): string => {
  const asset = avatarManifest[assetId];
  return asset ? asset.name : assetId;
};

// Helper function to get asset requirements from manifest
const getAssetRequirement = (assetId: string, avatarManifest: Record<string, any>): string => {
  const asset = avatarManifest[assetId];
  if (!asset) return 'Unknown requirement';

  switch (asset.unlockType) {
    case 'xp':
      return `Earn ${asset.unlockAt} XP`;
    case 'exercises':
      return `Complete ${asset.unlockAt} exercises`;
    case 'practiceTest':
      return `Complete ${asset.unlockAt} practice tests`;
    case 'quiz':
      return `Complete ${asset.unlockAt} quizzes`;
    case 'certificate':
      return `Earn ${asset.unlockAt} certificates`;
    case 'streak':
      return `Maintain ${asset.unlockAt} day streak`;
    case 'challenge':
      return `Complete ${asset.unlockAt} challenges`;
    case 'subscriptionMonths':
      return `Subscribe for ${asset.unlockAt} months`;
    case 'specialDate':
      return `Special date: ${asset.unlockAt}`;
    case 'plan':
      return `Plan: ${asset.unlockAt.charAt(0).toUpperCase() + asset.unlockAt.slice(1).toLowerCase()}`;
    case 'fullAccess':
      return 'Full access';
    case 'free':
      return 'Free for everyone';
    default:
      return 'Complete requirements';
  }
};

const Achievements = ({ assets, className }: IAchievementsProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const avatarManifest = useAvatarManifest();

  // Filter assets to only those present in avatarManifest
  const filteredAssets = assets.filter((asset) => avatarManifest[asset.assetId]);

  // Helper function to format date from ISO string
  const formatClaimedDate = (claimedAt?: string): string => {
    if (!claimedAt) return 'Unknown';

    const date = new Date(claimedAt);
    const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    const month = monthNames[date.getMonth()];
    const day = date.getDate();

    return `${month} ${day}`;
  };

  // Sort assets by claimedAt date (most recent first)
  const sortedAssets = [...filteredAssets].sort((a, b) => {
    if (!a.claimedAt) return 1;
    if (!b.claimedAt) return -1;
    return new Date(b.claimedAt).getTime() - new Date(a.claimedAt).getTime();
  });

  // Check scroll position and update indicators
  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  // Scroll functions
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -240, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 240, behavior: 'smooth' });
    }
  };

  // Set up scroll listeners
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      checkScrollPosition();
      container.addEventListener('scroll', checkScrollPosition);
      return () => container.removeEventListener('scroll', checkScrollPosition);
    }
    return undefined;
  }, [sortedAssets]);

  // Update scroll indicators when assets change
  useEffect(() => {
    checkScrollPosition();
  }, [sortedAssets]);

  return (
    <SectionWrapper headerText='Achievements' className={`${styles.body_wrapper} ${className}`}>
      <div className={styles.header}>
        <div className={styles.itemCount}>{filteredAssets.length} items</div>
      </div>

      <div className={styles.subHeader}>
        <span>Items Unlocked</span>
        <span>
          {filteredAssets.length}/{Object.keys(avatarManifest).length} items
        </span>
      </div>

      <div className={styles.scrollWrapper}>
        {canScrollLeft && (
          <button type='button' className={`${styles.scrollButton} ${styles.scrollLeft}`} onClick={scrollLeft} aria-label='Scroll left'>
            &#8249;
          </button>
        )}

        <div ref={scrollContainerRef} className={styles.itemsGrid}>
          {sortedAssets.map((asset, index) => {
            const avatarItem = avatarManifest[asset.assetId];
            return (
              <div key={asset.assetId} className={styles.achievementItem}>
                <div className={styles.itemIcon}>
                  {avatarItem && (avatarItem.previewSvg || avatarItem.svg) ? (
                    <img
                      src={avatarItem.previewSvg || avatarItem.svg}
                      alt={avatarItem.name}
                      className={styles.avatarSvg}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        transform: ['tshirt-yellow', 'tshirt-green', 'tshirt-black'].includes(asset.assetId) ? undefined : 'scale(2)',
                      }}
                    />
                  ) : (
                    <div className={styles.trenchCoat} />
                  )}
                </div>
                <div className={styles.itemInfo}>
                  <div className={styles.itemName}>{getAssetName(asset.assetId, avatarManifest)}</div>
                  <div className={styles.itemRequirement}>{getAssetRequirement(asset.assetId, avatarManifest)}</div>
                </div>
                <div className={styles.itemDate}>{formatClaimedDate(asset.claimedAt)}</div>
              </div>
            );
          })}
        </div>

        {canScrollRight && (
          <button type='button' className={`${styles.scrollButton} ${styles.scrollRight}`} onClick={scrollRight} aria-label='Scroll right'>
            &#8250;
          </button>
        )}
      </div>
    </SectionWrapper>
  );
};

Achievements.defaultProps = {
  className: '',
};

export default Achievements;
